import * as Api from '@ViewModels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { ADMIN_ACCOUNT_OPEN_INVOICE_SUMMARY_QUERY_KEY } from './useAdminAccountOpenInvoiceSummaryQuery';

export const ADMIN_ACCOUNT_OPEN_INVOICES_BY_ID_QUERY_KEY = 'admin/account/<accountId>/openInvoices';

/** Mirrors the API's `OverdueGraceDays`: no payment terms are recorded, so an invoice is overdue 30 days past due. */
export const OPEN_INVOICE_OVERDUE_GRACE_DAYS = 30;

const owedTotals = (invoices: Api.IChargifyInvoice[]) => {
	const owed = invoices.filter(invoice => invoice.dueAmount > 0);
	// Due dates are UTC-midnight calendar dates, so compare them as YYYY-MM-DD strings against a local-date cutoff.
	const overdueCutoff = moment().subtract(OPEN_INVOICE_OVERDUE_GRACE_DAYS, 'days').format('YYYY-MM-DD');
	return {
		count: owed.length,
		total: owed.reduce((sum, invoice) => sum + invoice.dueAmount, 0),
		overdue: owed.some(
			invoice => !!invoice.dueDate && moment.utc(invoice.dueDate).format('YYYY-MM-DD') < overdueCutoff
		),
	};
};

/**
 * Live open invoices for one account. The API also refreshes the account's stored open-invoice summary when it has
 * drifted, so when the live totals or overdue state disagree with the summary we were rendering, the summary query is
 * refetched to bring the status chip and PAST DUE label in line.
 */
export function useAdminAccountOpenInvoicesByIdQuery({
	accountId,
	storedSummary,
	enabled = true,
}: {
	accountId: string;
	storedSummary: Api.IOpenInvoiceSummary | null;
	enabled?: boolean;
}) {
	const userSession = useAdminUserSession();
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: [ADMIN_ACCOUNT_OPEN_INVOICES_BY_ID_QUERY_KEY, accountId],
		queryFn: async () => {
			const invoices = await userSession.webServiceHelper.callAsync<Api.IChargifyInvoice[]>(
				`admin/account/${encodeURIComponent(accountId)}/openInvoices`,
				'GET'
			);
			const live = owedTotals(invoices ?? []);
			const storedCount = storedSummary?.invoiceCount ?? 0;
			const storedTotal = storedSummary?.dueAmount ?? 0;
			const storedOverdue = storedSummary?.status === Api.OpenInvoiceStatus.Overdue;
			if (live.count !== storedCount || Math.abs(live.total - storedTotal) >= 0.01 || live.overdue !== storedOverdue) {
				queryClient.invalidateQueries({ queryKey: [ADMIN_ACCOUNT_OPEN_INVOICE_SUMMARY_QUERY_KEY, accountId] });
			}
			return invoices ?? [];
		},
		enabled: enabled && !!accountId,
		retry: false,
		meta: { toastErrorMessage: 'Unable to load open invoices' },
	});
}
